/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 31/08/2026 | Lic. José Luis Aragón Cervantes  | Errores HTTP no cierran sesion; se registran en log local
 *      3       | 22/09/2026 | Lic. José Luis Aragón Cervantes  | 401 -> refreshSession single-flight -> un retry con token
 *      4       | 23/09/2026 | LIC. José Luis Aragón C.                       | Ortografía: caracteres reparados a UTF-8
 *                 |            |                                  fresco; 403 intacto; refresh excluido del retry
 *      5       | 05/10/2026 | LIC. José Luis Aragón C.                       | fetchPdfBlob aditivo: Blob sin abrir/descargar
 * ---------------------------------------------------------------------------------------
 */
import { useState } from "react"
import { createContext, useContext } from "react";
import { useLayout } from "./LayoutContext";

import { getResponseModel } from "../../settings/responseModel";
import { isJsonString } from "../../settings/utils";
import { registerRequestErrorLog } from "../../settings/errorLogStorage";
import { getSessionRefresh, getSessionLogout, isSessionRefreshRequest } from "../../settings/sessionRefreshBridge";

export const RequestContext = createContext();
export const RequestProvider = (props) => {
    const { handleOpenAlert, handleOpenLoader, handleCloseLoader } = useLayout();
    const [token, setToken] = useState("");
    const tenantAccessTokenName = "sizesoftx-tenant-access-token";
    const tokenName = "sizesoftx-dashboard-token";
    const getAuthorizationToken = () => localStorage.getItem(tenantAccessTokenName) || token || localStorage.getItem(tokenName) || "";

    const createTimeoutSignal = (ms) => {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), ms ?? 60000);
        return { signal: controller.signal, clear: () => clearTimeout(id) };
    };

    const errorMessage = "Ocurrió un error al consumir el recurso solicitado";
    const errorForbiden = "No cuenta con los permisos necesarios para consumir este recurso";
    const errorNotFound = "No se encontró el recurso solicitado";

    const logRequestFailure = (payload = {}, apiResponse = {}, message = errorMessage) => {
        registerRequestErrorLog({
            httpCode: apiResponse?.status ?? 0,
            method: payload?.method ?? "FETCH",
            url: payload?.url ?? apiResponse?.url ?? "",
            message,
            source: "RequestContext",
            severity: apiResponse?.status === 401 || apiResponse?.status === 403 ? "warning" : "error",
        });
    };

    const responseValidatorJson = async function (apiResponse, payload = {}) {
        if (apiResponse.status === 403) {
            logRequestFailure(payload, apiResponse, errorForbiden);
            return {
                hasError: true,
                httpCode: 403,
                message: errorForbiden,
            };
        }

        if (apiResponse.status === 401) {
            logRequestFailure(payload, apiResponse, "Respuesta no autorizada. La sesion se conserva para diagnostico.");
            return {
                hasError: true,
                httpCode: 401,
                message: "No fue posible validar la autorizacion del recurso. El incidente quedo registrado.",
            };
        }

        if (apiResponse.status !== 200) {
            logRequestFailure(payload, apiResponse, errorMessage);
            return {
                hasError: true,
                httpCode: apiResponse.status,
                message: errorMessage,
            };
        }

        return await apiResponse.json();
    };

    const responseValidatorBlob = async function (apiResponse, payload = {}) {
        if (apiResponse.status === 403) {
            logRequestFailure(payload, apiResponse, errorForbiden);
            return {
                hasError: true,
                httpCode: 403,
                message: errorForbiden,
            };
        }

        if (apiResponse.status === 401) {
            logRequestFailure(payload, apiResponse, "Respuesta no autorizada. La sesion se conserva para diagnostico.");
            return {
                hasError: true,
                httpCode: 401,
                message: "No fue posible validar la autorizacion del recurso. El incidente quedo registrado.",
            };
        }

        if (apiResponse.status === 404) {
            logRequestFailure(payload, apiResponse, errorNotFound);
            return {
                hasError: true,
                httpCode: 404,
                message: errorNotFound,
            };
        }

        if (apiResponse.status !== 200) {
            logRequestFailure(payload, apiResponse, errorMessage);
            return {
                hasError: true,
                httpCode: apiResponse.status,
                message: errorMessage,
            };
        }

        return {
            hasError: false,
            httpCode: 200,
            result: await apiResponse.blob(),
        };
    };

    const getTokenPreview = () => {
        const t = getAuthorizationToken();
        if (!t) return "(sin token)";
        return t.length > 70 ? t.slice(0, 50) + "..." + t.slice(-20) : t;
    };

    // Núcleo del retry-401. NO cambia firmas, payloads, serialización, blobs ni timeouts:
    // solo decide si el fetch original se repite una vez con credenciales renovadas.
    // - 401 -> invoca la MISMA refreshSession() de SesionContext (single-flight en
    //   refreshPromiseRef: N peticiones 401 simultáneas esperan la misma promesa; aquí
    //   no existe otro mecanismo de refresh ni otro single-flight).
    // - El retry relee getAuthorizationToken() DESPUÉS del refresh exitoso: nunca
    //   reutiliza el header Authorization original (que porta el JWE/JWT expirado).
    // - Máximo UN retry por petición original (sin recursión: el segundo fetch se
    //   devuelve tal cual aunque sea 401).
    // - La petición TenantSeguridad/RefreshToken nunca entra aquí (anti-recursión).
    // - 403 no pasa por aquí: los validadores lo devuelven como permiso, sin refresh.
    // - Refresh fallido: se invoca el logout DE SesionContext (una sola vez por su
    //   guard); RequestContext no implementa logout propio. Se devuelve la respuesta
    //   401 original para que los validadores mantengan su forma actual.
    const fetchWithAuthRetry = async (url, buildInit) => {
        let apiResponse = await fetch(url, buildInit(getAuthorizationToken()));

        if (apiResponse.status !== 401 || isSessionRefreshRequest(url)) {
            return apiResponse;
        }

        const refresh = getSessionRefresh();
        let refreshed = null;
        try {
            refreshed = refresh
                ? await refresh({ navigateAfterSave: false, mostrarLoader: false })
                : { ok: false };
        } catch {
            refreshed = { ok: false };
        }

        if (!refreshed || !refreshed.ok) {
            try {
                getSessionLogout()?.();
            } catch {}
            return apiResponse;
        }

        return fetch(url, buildInit(getAuthorizationToken()));
    };

    const GetRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }
        try {
            const tokenPreview = getTokenPreview();
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "GET",
                headers: {
                    "Authorization": "Bearer " + authToken,
                },
                signal,
            }));
            clear();
            response = await responseValidatorJson(apiResponse, { ...payload, method: "GET" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    if (!isJsonString(response.message))
                        handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    const GetFileRequest = async function (payload) {
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        let urlEnd = payload.url;

        if (payload.query) {
            const queryString =
                "?" +
                Object.keys(payload.query)
                    .map((x) => `${x}=${payload.query[x]}`)
                    .join("&");
            urlEnd += queryString;
        }

        try {
            let apiResponse = await fetchWithAuthRetry(urlEnd, (authToken) => ({
                method: "GET",
                headers: {
                    "Authorization": "Bearer " + authToken,
                },
                signal,
            }));
            clear();

            const response = await responseValidatorBlob(apiResponse, { ...payload, method: "GET" });
            if (response.hasError) {
                handleOpenAlert(response.message);
                handleCloseLoader();
                return false;
            }

            var url = window.URL.createObjectURL(response.result);
            if (payload.openInNewTab) {
                const openedWindow = window.open(url, "_blank");
                if (openedWindow) {
                    handleCloseLoader();
                    return true;
                }
            }

            var a = document.createElement("a");
            a.href = url;
            a.download = payload.filename ? payload.filename : a.download;
            document.body.appendChild(a);
            a.click();
            a.remove();
            handleCloseLoader();
            return true;
        } catch (error) {
            clear();
            handleOpenAlert(errorMessage,"error");
        }

        handleCloseLoader();

        return false;
    };

    const PostFileRequest = async function (payload) {
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        let urlEnd = payload.url;

        try {
            let apiResponse = await fetchWithAuthRetry(urlEnd, (authToken) => ({
                method: "POST",
                headers: {
                    "Authorization": "Bearer " + authToken,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload.body),
                signal,
            }));
            clear();

            const response = await responseValidatorBlob(apiResponse, { ...payload, method: "POST" });
            if (response.hasError) {
                handleOpenAlert(response.message);
                handleCloseLoader();
                return false;
            }

            var url = window.URL.createObjectURL(response.result);
            if (payload.openInNewTab) {
                const openedWindow = window.open(url, "_blank");
                if (openedWindow) {
                    handleCloseLoader();
                    return true;
                }
            }

            var a = document.createElement("a");
            a.href = url;
            a.download = payload.filename ? payload.filename : a.download;
            document.body.appendChild(a);
            a.click();
            a.remove();
            handleCloseLoader();
            return true;
        } catch (error) {
            clear();
            handleOpenAlert(errorMessage,"error");
        }

        handleCloseLoader();

        return false;
    };

    const PostRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            const tokenPreview = getTokenPreview();
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "POST",
                headers: {
                    "Authorization": "Bearer " + authToken,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload.body),
                signal,
            }));
            clear();

            response = await responseValidatorJson(apiResponse, { ...payload, method: "POST" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    const PostFormRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "POST",
                headers: {
                    "Authorization": "Bearer " + authToken,
                    "Content-Type": "application/json",
                },
                body: payload.body,
                signal,
            }));
            clear();

            response = await responseValidatorJson(apiResponse, { ...payload, method: "POST" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    const PutRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "PUT",
                headers: {
                    "Authorization": "Bearer " + authToken,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload.body),
                signal,
            }));
            clear();

            response = await responseValidatorJson(apiResponse, { ...payload, method: "PUT" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    const PutFormRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "PUT",
                headers: {
                    "Authorization": "Bearer " + authToken,
                },
                body: payload.body,
                signal,
            }));
            clear();

            response = await responseValidatorJson(apiResponse, { ...payload, method: "PUT" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    const DeleteRequest = async function (payload) {
        let response = getResponseModel();
        const { signal, clear } = createTimeoutSignal(payload.timeout);

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            let apiResponse = await fetchWithAuthRetry(payload.url, (authToken) => ({
                method: "DELETE",
                headers: {
                    "Authorization": "Bearer " + authToken,
                },
                signal,
            }));
            clear();

            response = await responseValidatorJson(apiResponse, { ...payload, method: "DELETE" });

            if (payload.alert) {
                if (!response.hasError) {
                    handleOpenAlert(response.message, "success");
                } else {
                    handleOpenAlert(response.message);
                }
            }
        } catch (error) {
            clear();
            response.hasError = true;
            response.httpCode = 500;
            response.message = errorMessage;

            handleOpenAlert(response.message);
        }

        handleCloseLoader();

        return response;
    };

    // Rev 5 (05/10/2026): descarga de PDF como Blob SIN abrir ni descargar.
    // Aditiva: no modifica GetFileRequest/PostFileRequest ni sus callers.
    // Retorna { ok:true, blob } o { ok:false, message }. Detecta el caso
    // "error JSON con HTTP 200" por content-type para no mostrar basura como
    // si fuera PDF. No dispara alertas: el caller decide qué mostrar.
    // payload: { url, method: "GET"|"POST", body, timeout, loader }
    const fetchPdfBlob = async function (payload = {}) {
        const { signal, clear } = createTimeoutSignal(payload.timeout);
        const method = (payload.method || "GET").toUpperCase();

        if (payload.loader) {
            handleOpenLoader(payload.loader);
        }

        try {
            const initBuilder = (authToken) => {
                const headers = { "Authorization": "Bearer " + authToken };
                const init = { method, headers, signal };
                if (method === "POST") {
                    headers["Content-Type"] = "application/json";
                    init.body = JSON.stringify(payload.body ?? {});
                }
                return init;
            };
            const apiResponse = await fetchWithAuthRetry(payload.url, initBuilder);
            clear();

            if (apiResponse.status === 403) {
                logRequestFailure({ ...payload, method }, apiResponse, errorForbiden);
                return { ok: false, message: errorForbiden };
            }
            if (apiResponse.status === 401) {
                logRequestFailure({ ...payload, method }, apiResponse, "Respuesta no autorizada. La sesion se conserva para diagnostico.");
                return { ok: false, message: "No fue posible validar la autorizacion del recurso. El incidente quedo registrado." };
            }
            if (apiResponse.status === 404) {
                logRequestFailure({ ...payload, method }, apiResponse, errorNotFound);
                return { ok: false, message: errorNotFound };
            }
            if (apiResponse.status !== 200) {
                logRequestFailure({ ...payload, method }, apiResponse, errorMessage);
                return { ok: false, message: errorMessage };
            }

            const contentType = (apiResponse.headers?.get("content-type") || "").toLowerCase();
            if (contentType.includes("application/json")) {
                let message = errorMessage;
                try {
                    const data = await apiResponse.json();
                    message = data?.message || data?.Message || message;
                } catch {}
                logRequestFailure({ ...payload, method }, apiResponse, message);
                return { ok: false, message };
            }

            const blob = await apiResponse.blob();
            if (!blob || blob.size === 0) {
                return { ok: false, message: "El servidor devolvió un documento vacío" };
            }
            return { ok: true, blob };
        } catch (error) {
            clear();
            const message = error?.name === "AbortError"
                ? "La solicitud excedió el tiempo de espera"
                : errorMessage;
            logRequestFailure({ ...payload, method }, {}, message);
            return { ok: false, message };
        } finally {
            handleCloseLoader();
        }
    };

    return (
        <RequestContext.Provider
            value={{
                GetRequest,
                GetFileRequest,
                PostFileRequest,
                fetchPdfBlob,
                PostRequest,
                PostFormRequest,
                PutRequest,
                PutFormRequest,
                DeleteRequest,
                token,setToken,
            }}
        >
            {props.children}
        </RequestContext.Provider>
    );
};

export const useRequest = () => useContext(RequestContext);
